import { Flex, Skeleton } from "@radix-ui/themes";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";

// import PythIcon from "@assets/icons/pyth.svg?react";
import { useStores } from "@stores";
import { theme } from "@themes/themes";
import { formatPrice } from "@utils/Format";

import { MarkPriceText, MonoText } from "./styles";
import { formatCompact } from "./utils";

type PriceDirection = "up" | "down" | "neutral";

// Mark Price display with direction color - isolated observer
export const MarkPriceDisplay = observer(() => {
  const { marketStore } = useStores();
  const market = marketStore.activeMarket;
  const prevPriceRef = useRef<string | null>(null);
  const [priceDirection, setPriceDirection] = useState<PriceDirection>("neutral");

  const currentPrice = market?.mark_price;

  useEffect(() => {
    if (currentPrice && prevPriceRef.current) {
      const curr = Number(currentPrice);
      const prev = Number(prevPriceRef.current);
      if (curr > prev) {
        setPriceDirection("up");
      } else if (curr < prev) {
        setPriceDirection("down");
      }
    }
    prevPriceRef.current = currentPrice ?? null;
  }, [currentPrice]);

  const priceColorValue =
    priceDirection === "up"
      ? theme.color("green-strong")
      : priceDirection === "down"
        ? theme.color("red-strong")
        : theme.color("accent-contrast");

  return (
    <Skeleton height="24px" loading={!market} width="120px">
      <MarkPriceText data-testid="market-data:mark-price:value" style={{ color: priceColorValue }}>
        ${formatPrice(market?.mark_price)}
      </MarkPriceText>
    </Skeleton>
  );
});

// Oracle Price display - isolated observer
export const OraclePriceDisplay = observer(() => {
  const { marketStore } = useStores();
  const market = marketStore.activeMarket;

  return (
    <Skeleton height="18px" loading={!market} width="100px">
      <Flex align="center" gap="4px" minWidth="100px">
        <MonoText data-testid="market-data:oracle-price:value" font="body14">
          ${formatPrice(market?.oracle_price)}
        </MonoText>
        {/* <PythIcon data-testid="market-data:oracle-price:icon" /> */}
      </Flex>
    </Skeleton>
  );
});

// Volume display - isolated observer
export const VolumeDisplay = observer(() => {
  const { marketStore } = useStores();
  const market = marketStore.activeMarket;

  return (
    <Skeleton height="18px" loading={!market} width="65px">
      <Flex minWidth="65px">
        <MonoText data-testid="market-data:volume-24h" font="body14">
          ${formatCompact(market?.turnover_24h)}
        </MonoText>
      </Flex>
    </Skeleton>
  );
});

// Open Interest display - isolated observer
export const OpenInterestDisplay = observer(() => {
  const { marketStore } = useStores();
  const market = marketStore.activeMarket;

  return (
    <Skeleton height="18px" loading={!market} width="60px">
      <Flex minWidth="60px">
        <MonoText data-testid="market-data:open-interest" font="body14">
          ${formatCompact(market?.open_interest_value)}
        </MonoText>
      </Flex>
    </Skeleton>
  );
});

// High 24h display - isolated observer
export const High24hDisplay = observer(() => {
  const { marketStore } = useStores();
  const market = marketStore.activeMarket;
  const marketData = marketStore.getMarketData(market?.addr ?? "");

  return (
    <Skeleton height="18px" loading={!market} width="60px">
      <MonoText font="body14">{marketData.high24h || "-"}</MonoText>
    </Skeleton>
  );
});

// Low 24h display - isolated observer
export const Low24hDisplay = observer(() => {
  const { marketStore } = useStores();
  const market = marketStore.activeMarket;
  const marketData = marketStore.getMarketData(market?.addr ?? "");

  return (
    <Skeleton height="18px" loading={!market} width="60px">
      <MonoText font="body14">{marketData.low24h || "-"}</MonoText>
    </Skeleton>
  );
});
